import React, { useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

function DateSelector({ selectedDate, onDateChange, showCalendar, onToggleCalendar }) {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ];

  const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  // ── Date boundaries ───────────────────────────────────────
  // Build min/max as local date strings to avoid UTC shift
  function toLocalDateStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const today    = new Date();
  const minDate  = new Date(today);
  minDate.setDate(today.getDate() - 7);

  const todayStr = toLocalDateStr(today);
  const minStr   = toLocalDateStr(minDate);

  function isDateDisabled(day) {
    if (!day) return true;
    const y = currentYear;
    const m = String(currentMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    // Disabled if before minDate OR after today
    return dateStr < minStr || dateStr > todayStr;
  }

  function formatDate(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
  }

  function getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  function getFirstDayOfMonth(month, year) {
    return new Date(year, month, 1).getDay();
  }

  function generateCalendarDays() {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay    = getFirstDayOfMonth(currentMonth, currentYear);
    const days        = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(day);
    return days;
  }

  function handleDateSelect(day) {
    if (!day || isDateDisabled(day)) return;
    const year     = currentYear;
    const month    = String(currentMonth + 1).padStart(2, '0');
    const dayPadded = String(day).padStart(2, '0');
    onDateChange(`${year}-${month}-${dayPadded}`);
    onToggleCalendar();
  }

  function isSelectedDate(day) {
    if (!day || !selectedDate) return false;
    const [year, month, dayOfMonth] = selectedDate.split('-').map(Number);
    return (
      dayOfMonth === day &&
      (month - 1) === currentMonth &&
      year === currentYear
    );
  }

  // Prevent navigating to months entirely outside the valid range
  function canGoPrevious() {
    // Allow if the previous month contains any selectable days
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear  = currentMonth === 0 ? currentYear - 1 : currentYear;
    // Last day of previous month
    const lastDayOfPrev = new Date(prevYear, prevMonth + 1, 0);
    return toLocalDateStr(lastDayOfPrev) >= minStr;
  }

  function canGoNext() {
    // Allow if the next month contains any selectable days
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear  = currentMonth === 11 ? currentYear + 1 : currentYear;
    // First day of next month
    const firstDayOfNext = new Date(nextYear, nextMonth, 1);
    return toLocalDateStr(firstDayOfNext) <= todayStr;
  }

  function goToPreviousMonth() {
    if (!canGoPrevious()) return;
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  }

  function goToNextMonth() {
    if (!canGoNext()) return;
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  }

  const calendarDays = generateCalendarDays();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3 sm:p-4">
      <div className="relative">
        <label className="flex items-center gap-2 mb-2 text-xs font-semibold text-slate-800 font-montserrat">
          <Calendar className="w-3.5 h-3.5 text-[#B1336E]" />
          Fecha Seleccionada
        </label>

        <button
          type="button"
          onClick={onToggleCalendar}
          className="w-full rounded-lg border-2 border-slate-300 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all flex items-center justify-between font-montserrat hover:border-[#B1336E]"
        >
          <span className="text-slate-800 font-medium">
            {selectedDate ? formatDate(selectedDate) : 'Selecciona una fecha'}
          </span>
          <Calendar className="w-4 h-4 text-slate-400" />
        </button>

        {showCalendar && (
          <div className="absolute top-full left-0 mt-2 w-full max-w-xs bg-white rounded-lg shadow-xl border border-slate-200 p-3 z-30">

            {/* Month/Year header */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={goToPreviousMonth}
                disabled={!canGoPrevious()}
                className="p-1 hover:bg-slate-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>

              <p className="text-xs font-bold text-slate-800 font-montserrat">
                {monthNames[currentMonth]} {currentYear}
              </p>

              <button
                type="button"
                onClick={goToNextMonth}
                disabled={!canGoNext()}
                className="p-1 hover:bg-slate-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Day names */}
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {dayNames.map((day, i) => (
                <div key={i} className="text-center text-[10px] font-semibold text-slate-500 py-1 font-montserrat">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-0.5">
              {calendarDays.map((day, index) => {
                const isSelected = isSelectedDate(day);
                const disabled   = isDateDisabled(day);

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDateSelect(day)}
                    disabled={disabled}
                    className={`
                      aspect-square rounded text-xs transition-all font-montserrat
                      ${!day ? 'invisible' : ''}
                      ${disabled && day ? 'text-slate-300 cursor-not-allowed' : ''}
                      ${isSelected ? 'bg-[#B1336E] text-white font-bold hover:bg-[#B1336E]/90' : ''}
                      ${!disabled && !isSelected ? 'text-slate-700 hover:bg-[#B1336E]/10' : ''}
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Footer hint */}
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5">
              <p className="text-center text-[10px] text-slate-400 font-montserrat">
                Disponible: últimos 7 días hasta hoy
              </p>
              <button
                type="button"
                onClick={onToggleCalendar}
                className="w-full py-1.5 text-xs text-slate-600 hover:text-slate-800 transition-colors font-montserrat"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DateSelector;